import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEditor } from '@tiptap/react';
import { CheckCircle2, Circle, MessageSquare, Trash2, Send } from 'lucide-react';
import { commentsApi, type Comment } from '../api/comments';
import { membersApi, type Member } from '../api/members';
import { toast } from './Toast';
import { formatRelativeTime, getCollaboratorColor, getInitials } from '../lib/utils';

interface CommentSectionProps {
  docId: string;
  wsId: string;
  editor: ReturnType<typeof useEditor>;
  currentUserId?: string;
}

export function CommentSection({ docId, wsId, editor, currentUserId }: CommentSectionProps) {
  const queryClient = useQueryClient();
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [showMentionSearch, setShowMentionSearch] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionTarget, setMentionTarget] = useState<'comment' | 'reply'>('comment');
  const mentionInputRef = useRef<HTMLTextAreaElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['comments', docId],
    queryFn: () => commentsApi.list(docId),
    enabled: !!docId,
  });

  const { data: members = [] } = useQuery({
    queryKey: ['members', wsId],
    queryFn: () => membersApi.list(wsId),
    enabled: !!wsId,
  });

  const filteredMembers = mentionQuery
    ? members.filter((m) => m.nickname?.toLowerCase().includes(mentionQuery.toLowerCase()) || m.email.toLowerCase().includes(mentionQuery.toLowerCase()))
    : members;

  const createMutation = useMutation({
    mutationFn: (data: { body: string; anchor?: string; parentId?: string }) =>
      commentsApi.create(docId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', docId] });
      setCommentBody('');
      setReplyBody('');
      setReplyTo(null);
      setShowCommentInput(false);
      toast.success('评论已发送');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '评论发送失败');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (commentId: string) => commentsApi.remove(docId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', docId] });
      toast.success('评论已删除');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '删除失败');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (commentId: string) => commentsApi.resolve(docId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', docId] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '操作失败');
    },
  });

  const unresolveMutation = useMutation({
    mutationFn: (commentId: string) => commentsApi.unresolve(docId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', docId] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '操作失败');
    },
  });

  function handleSendComment() {
    if (!commentBody.trim()) return;
    const anchor = editor ? `${editor.state.selection.from}-${editor.state.selection.to}` : undefined;
    createMutation.mutate({ body: commentBody.trim(), anchor });
  }

  function handleSendReply(parentId: string) {
    if (!replyBody.trim()) return;
    createMutation.mutate({ body: replyBody.trim(), parentId });
  }

  function handleMentionSelect(member: Member) {
    const name = member.nickname || member.email.split('@')[0];
    const mentionText = `@${name} `;
    if (mentionTarget === 'comment') {
      setCommentBody((prev) => prev.replace(/@\S*$/, mentionText));
    } else {
      setReplyBody((prev) => prev.replace(/@\S*$/, mentionText));
    }
    setShowMentionSearch(false);
    setMentionQuery('');
  }

  function handleCommentInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setCommentBody(value);
    const atMatch = value.match(/@(\S*)$/);
    if (atMatch) {
      setShowMentionSearch(true);
      setMentionQuery(atMatch[1]);
      setMentionTarget('comment');
    } else {
      setShowMentionSearch(false);
    }
  }

  function handleReplyInputChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setReplyBody(value);
    const atMatch = value.match(/@(\S*)$/);
    if (atMatch) {
      setShowMentionSearch(true);
      setMentionQuery(atMatch[1]);
      setMentionTarget('reply');
    } else {
      setShowMentionSearch(false);
    }
  }

  const topLevelComments = comments.filter((c) => !c.parentId);

  return (
    <div className="flex h-full flex-col border-l border-border bg-bg-surface">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-medium">评论</h3>
        <MessageSquare className="h-4 w-4 text-text-tertiary" />
      </div>

      <div className="flex-1 overflow-auto p-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 animate-shimmer rounded-lg bg-bg-subtle" />
            ))}
          </div>
        ) : topLevelComments.length === 0 && !showCommentInput ? (
          <div className="flex flex-col items-center py-12 text-center">
            <MessageSquare className="h-8 w-8 text-text-tertiary" />
            <p className="mt-2 text-sm text-text-tertiary">暂无评论</p>
            <p className="text-xs text-text-tertiary">选中文本后添加评论</p>
          </div>
        ) : (
          <div className="space-y-4">
            {topLevelComments.map((comment) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                currentUserId={currentUserId}
                onReply={() => {
                  setReplyTo(comment.id);
                  setReplyBody('');
                  setTimeout(() => replyInputRef.current?.focus(), 0);
                }}
                onDelete={(id) => {
                  if (confirm('确定删除此评论？')) {
                    removeMutation.mutate(id);
                  }
                }}
                onResolve={() => resolveMutation.mutate(comment.id)}
                onUnresolve={() => unresolveMutation.mutate(comment.id)}
                replies={comment.replies}
              >
                {replyTo === comment.id && (
                  <div className="mt-2">
                    <div className="relative">
                      <textarea
                        ref={replyInputRef}
                        value={replyBody}
                        onChange={handleReplyInputChange}
                        placeholder="输入回复…"
                        className="input min-h-[60px] resize-none text-sm"
                        rows={2}
                      />
                      {showMentionSearch && mentionTarget === 'reply' && (
                        <MentionPopover
                          members={filteredMembers}
                          onSelect={handleMentionSelect}
                        />
                      )}
                    </div>
                    <div className="mt-1 flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setReplyTo(null)}
                        className="btn btn-ghost btn-xs"
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        disabled={!replyBody.trim() || createMutation.isPending}
                        onClick={() => handleSendReply(comment.id)}
                        className="btn btn-primary btn-xs"
                      >
                        <Send className="h-3 w-3" /> 回复
                      </button>
                    </div>
                  </div>
                )}
              </CommentCard>
            ))}
          </div>
        )}

        {/* 新建评论输入框 */}
        {showCommentInput && (
          <div className="mt-4 border-t border-border pt-4">
            <div className="relative">
              <textarea
                ref={mentionInputRef}
                value={commentBody}
                onChange={handleCommentInputChange}
                placeholder="输入评论内容…"
                className="input min-h-[80px] resize-none text-sm"
                rows={3}
              />
              {showMentionSearch && mentionTarget === 'comment' && (
                <MentionPopover
                  members={filteredMembers}
                  onSelect={handleMentionSelect}
                />
              )}
            </div>
            <div className="mt-2 flex justify-end gap-1">
              <button
                type="button"
                onClick={() => {
                  setShowCommentInput(false);
                  setCommentBody('');
                }}
                className="btn btn-ghost btn-xs"
              >
                取消
              </button>
              <button
                type="button"
                disabled={!commentBody.trim() || createMutation.isPending}
                onClick={handleSendComment}
                className="btn btn-primary btn-xs"
              >
                <Send className="h-3 w-3" /> 发送
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** 单个评论卡片 */
function CommentCard({
  comment,
  currentUserId,
  onReply,
  onDelete,
  onResolve,
  onUnresolve,
  replies,
  children,
}: {
  comment: Comment;
  currentUserId?: string;
  onReply: () => void;
  onDelete: (id: string) => void;
  onResolve: () => void;
  onUnresolve: () => void;
  replies?: Comment[];
  children?: React.ReactNode;
}) {
  const isAuthor = comment.author.id === currentUserId;
  const displayName = comment.author.nickname || '匿名';

  return (
    <div className={`rounded-lg border ${comment.resolved ? 'border-success/30 bg-success/5' : 'border-border'} p-3`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full text-[9px] font-medium text-white"
            style={{ backgroundColor: getCollaboratorColor(comment.author.id) }}
          >
            {getInitials(displayName)}
          </span>
          <span className="text-sm font-medium">{displayName}</span>
          <span className="text-xs text-text-tertiary">{formatRelativeTime(comment.createdAt)}</span>
        </div>
        <div className="flex items-center gap-1">
          {comment.resolved ? (
            <button
              type="button"
              onClick={onUnresolve}
              className="flex h-6 w-6 items-center justify-center rounded text-success hover:bg-bg-subtle"
              title="取消解决"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onResolve}
              className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle hover:text-success"
              title="标记为解决"
            >
              <Circle className="h-3.5 w-3.5" />
            </button>
          )}
          {isAuthor && (
            <button
              type="button"
              onClick={() => onDelete(comment.id)}
              className="flex h-6 w-6 items-center justify-center rounded text-text-tertiary hover:bg-[#fef2f2] hover:text-danger"
              title="删除"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <p className="mt-1.5 text-sm text-text-primary whitespace-pre-wrap">{comment.body}</p>
      {comment.anchor && (
        <span className="mt-1 inline-block text-xs text-accent">引用了选中内容</span>
      )}
      <div className="mt-2">
        <button
          type="button"
          onClick={onReply}
          className="text-xs text-text-tertiary hover:text-text-primary"
        >
          回复
        </button>
      </div>

      {/* 回复列表 */}
      {replies && replies.length > 0 && (
        <div className="ml-4 mt-2 space-y-2 border-l-2 border-border pl-3">
          {replies.map((reply) => (
            <ReplyCard
              key={reply.id}
              reply={reply}
              currentUserId={currentUserId}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {children}
    </div>
  );
}

/** 回复卡片 */
function ReplyCard({
  reply,
  currentUserId,
  onDelete,
}: {
  reply: Comment;
  currentUserId?: string;
  onDelete: (id: string) => void;
}) {
  const isAuthor = reply.author.id === currentUserId;
  const displayName = reply.author.nickname || '匿名';

  return (
    <div className="rounded-md py-1">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span
            className="flex h-5 w-5 items-center justify-center rounded-full text-[8px] font-medium text-white"
            style={{ backgroundColor: getCollaboratorColor(reply.author.id) }}
          >
            {getInitials(displayName)}
          </span>
          <span className="text-xs font-medium">{displayName}</span>
          <span className="text-xs text-text-tertiary">{formatRelativeTime(reply.createdAt)}</span>
        </div>
        {isAuthor && (
          <button
            type="button"
            onClick={() => onDelete(reply.id)}
            className="flex h-5 w-5 items-center justify-center rounded text-text-tertiary hover:bg-[#fef2f2] hover:text-danger"
            title="删除"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
      <p className="mt-0.5 text-sm text-text-primary whitespace-pre-wrap">{reply.body}</p>
    </div>
  );
}

/** @提及弹出框 */
function MentionPopover({
  members,
  onSelect,
}: {
  members: Member[];
  onSelect: (member: Member) => void;
}) {
  return (
    <div className="absolute bottom-full left-0 right-0 mb-1 max-h-32 overflow-auto rounded-md border border-border bg-bg-surface shadow-md">
      {members.length === 0 ? (
        <div className="px-3 py-2 text-xs text-text-tertiary">无匹配成员</div>
      ) : (
        members.slice(0, 8).map((member) => (
          <button
            key={member.id}
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-bg-subtle"
            onClick={() => onSelect(member)}
          >
            <span
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[8px] font-medium text-white"
              style={{ backgroundColor: getCollaboratorColor(member.id) }}
            >
              {getInitials(member.nickname || member.email)}
            </span>
            <span className="truncate">{member.nickname || member.email.split('@')[0]}</span>
          </button>
        ))
      )}
    </div>
  );
}